import {SpaceTr, Tr} from "@/i18n/i18n";
import Scene = Phaser.Scene;
import {Keys} from "@/constants/key";
import TextUtils from "@/utils/text-utils";
import {ArtifactNotification, EffectType} from "@/store/artifact/types";
import {SquareButton} from "@/scenes.ui/square-button";
import {Fonts} from "@/constants/font";
import {VerticalStandardPanel} from "@/scenes.ui/vertical-standard-panel";
import Container = Phaser.GameObjects.Container;
import ArtifactUtils from "@/utils/artifact-utils";
import {ImageButton} from "@/scenes.ui/image-button";
import BaseSound = Phaser.Sound.BaseSound;
import SoundUtils from "@/utils/sound-utils";
import SpriteUtils from "@/utils/sprite-utils";
import CU from "@/utils/c-u";
import SkillUtils from "@/utils/skill-utils";

export class ArtifactUpdatesPanel extends VerticalStandardPanel {

    private centralContainer!: Container;

    private openingSound!: BaseSound | undefined;
    private clickSound!: BaseSound | undefined;

    constructor(scene: Scene, canvas: HTMLCanvasElement, nts: ArtifactNotification[], openingSound: BaseSound | undefined,
                clickSound: BaseSound | undefined, container?: Container) {
        super(scene, canvas, Tr('poka.vas.ne.bilo'), container);
        const self = this;
        this.openingSound = openingSound;
        this.clickSound = clickSound;

        this.centralContainer = new Container(scene);

        let updateText = TextUtils.addTextByFamily(scene, Tr('zaversheno.uluchshenie') + ' ' + (nts.length > 1 ? (String(nts.length) + SpaceTr('items.gen')) : ''),
            0, -170, '#ffd465', 17, 'normal', 400, Fonts.MYRIADPRO_BOLD);

        let attackSum = 0;
        let defenceSum = 0;

        for (let nt of nts) {
            if (nt.effectType === EffectType.ATTACK) {
                attackSum += (nt.nextLevelValue - nt.prevLevelValue);
            } else if (nt.effectType === EffectType.DEFENCE) {
                defenceSum += (nt.nextLevelValue - nt.prevLevelValue);
            } else if (nt.effectType === EffectType.ATTACK_DEFENCE) {
                attackSum += (nt.nextLevelValue - nt.prevLevelValue);
                defenceSum += (nt.nextLevelValue - nt.prevLevelValue);
            }
        }

        if (nts.length === 1) {
            let nt = nts[0];

            let lightFrom = SpriteUtils.addSprite(this.scene, -90, -90, Keys.ATLAS_PROFILE, Keys.UI_ART_LIGHT, 0.8);
            let lightTo = SpriteUtils.addSprite(this.scene, 90, -90, Keys.ATLAS_PROFILE, Keys.UI_ART_LIGHT, 0.8);
            let itemFrom = SpriteUtils.addSprite(this.scene, -90, -90, nt.atlasKey, nt.prevImageKey);
            let fromText = TextUtils.addTextAdvanced(scene, ArtifactUtils.getEffectText(nt.effectType, nt.prevLevelValue), -90, -50,
                '#e0e0e0', 15, 'normal', 150, 'center', 2, Fonts.ARIAL_MT);
            let itemArr = SpriteUtils.addSprite(this.scene, 0, -90, Keys.ATLAS_PROFILE, Keys.UI_ART_ARR);
            let itemTo = SpriteUtils.addSprite(this.scene, 90, -90, nt.atlasKey, nt.nextImageKey);
            let toText = TextUtils.addTextAdvanced(scene, ArtifactUtils.getEffectText(nt.effectType, nt.nextLevelValue), 90, -50,
                '#85ff85', 15, 'normal', 150, 'center', 2, Fonts.ARIAL_MT);
            this.add([lightFrom, lightTo, itemFrom, itemTo, itemArr, fromText, toText]);

        } else if (nts.length <= 4) {
            let cellWidth = Math.min(450 / nts.length, 120);
            let xPos = cellWidth / 2 - nts.length * cellWidth / 2;
            let yPos = -90;
            for (let nt of nts) {
                let itemImage = SpriteUtils.addSprite(this.scene, xPos, yPos, nt.atlasKey, nt.nextImageKey);
                SpriteUtils.setDispSize(itemImage, cellWidth / 1.5 / CU.S, cellWidth / 1.5 / CU.S);
                let effectText = TextUtils.addTextAdvanced(scene, nt.nextItemName, xPos, -32,
                    '#e5e5e5', 12, 'normal', 110, 'center', 2, Fonts.ARIAL_MT);
                this.add([itemImage, effectText]);
                xPos += cellWidth;
            }
        } else if (nts.length > 4) {
            this.initNavPanel(scene, nts, 0);
        }

        let sumLabel = TextUtils.addTextAdvanced(scene, nts.length > 1 ? Tr('summarniy.prirost') : Tr('prirost'), 0, 20,
            '#716f6a', 12, 'normal', 250, 'center', 2, Fonts.ARIAL_MT);

        if (attackSum > 0 && defenceSum > 0) {
            let attackEffect = TextUtils.addTextByFamily(scene, Tr('ataka.plus') + SkillUtils.formatValue(attackSum),
                -80, 50, '#9bff65', 17, 'normal', 400, Fonts.MYRIADPRO_BOLD);
            let defenceEffect = TextUtils.addTextByFamily(scene, Tr('zaschita.plus') + SkillUtils.formatValue(defenceSum),
                80, 50, '#ffb565', 17, 'normal', 400, Fonts.MYRIADPRO_BOLD);
            this.add([attackEffect, defenceEffect]);
        } else if (attackSum > 0) {
            let attackEffect = TextUtils.addTextByFamily(scene, Tr('ataka.plus') + SkillUtils.formatValue(attackSum),
                0, 50, '#9bff65', 17, 'normal', 400, Fonts.MYRIADPRO_BOLD);
            this.add(attackEffect);
        } else if (defenceSum > 0) {
            let defenceEffect = TextUtils.addTextByFamily(scene, Tr('zaschita.plus') + SkillUtils.formatValue(defenceSum),
                0, 50, '#ffb565', 17, 'normal', 400, Fonts.MYRIADPRO_BOLD);
            this.add(defenceEffect);
        }

        let okButton = new SquareButton(this.scene, 0, 155, 0, Keys.UI_ART_GREEN_BUTTON,
            Keys.ATLAS_PROFILE, Keys.UI_GOLDEN_CHECK, Tr('otlichno').toUpperCase(), false);

        okButton.setCallback(() => {
            self.close();
            SoundUtils.play(self.clickSound);
        });

        this.add([updateText, sumLabel, okButton]);
    }

    public open(): void {
        super.open();
        SoundUtils.play(this.openingSound);
    }

    private initNavPanel(scene: Scene, nts: ArtifactNotification[], startIndex: number) {
        this.centralContainer.setVisible(false);
        this.centralContainer.destroy();
        this.centralContainer = new Container(scene);
        this.add(this.centralContainer);
        let sublist = []
        while (startIndex < 0) {
            startIndex += nts.length;
        }
        sublist.push(nts[startIndex % nts.length]);
        sublist.push(nts[(startIndex + 1) % nts.length]);
        sublist.push(nts[(startIndex + 2) % nts.length]);

        let cellWidth = 120;
        let xPos = -120;
        let yPos = -90;
        for (let nt of sublist) {
            let itemImage = SpriteUtils.addSprite(this.scene, xPos, yPos, nt.atlasKey, nt.nextImageKey);
            SpriteUtils.setDispSize(itemImage, 80 / CU.S, 80 / CU.S);
            let effectText = TextUtils.addTextAdvanced(scene, nt.nextItemName, xPos, -32,
                '#e5e5e5', 12, 'normal', 110, 'center', 2, Fonts.ARIAL_MT);
            this.centralContainer.add([itemImage, effectText]);
            xPos += cellWidth;
        }

        const leftButton = new ImageButton(this.scene, -190, -90, Keys.ATLAS_PROFILE, Keys.UI_ARROW_LEFT);
        const rightButton = new ImageButton(this.scene, 190, -90, Keys.ATLAS_PROFILE, Keys.UI_ARROW_RIGHT);
        this.add(leftButton);
        this.add(rightButton);

        leftButton.setCallback(() => {
            this.initNavPanel(scene, nts, startIndex - 1);
        });
        rightButton.setCallback(() => {
            this.initNavPanel(scene, nts, startIndex + 1);
        });
    }
}
